<template>
  <div>
    <input :id="`id_${name}`" v-model="value" :name="name" type="hidden" />
    <label v-if="label || $slots.label" :for="name" class="input-label">
      <slot name="label" :required="required" :attrs-name="name"
        >{{ label }} <span v-if="required" class="required-input">*</span></slot
      >
    </label>
    <div class="relative">
      <div
        v-if="leadingIcon"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10"
      >
        <component :is="leadingIcon" class="h-5 w-5 text-gray-400" />
      </div>
      <!-- leading icon: => pl-10 / trailing-icon => pr-10 but also for chevron of select -->
      <button
        ref="buttonRef"
        type="button"
        class="px-3 bg-white relative border text-left cursor-default input-base leading-6 text-base"
        :class="{
          'pl-10': !!leadingIcon,
          'pr-10': displayedError,
          'border-gray-300 element-focus': !displayedError,
          'input-error': displayedError,
          'input-disabled': disabled,
          [classWidth]: true,
        }"
        aria-haspopup="listbox"
        aria-expanded="true"
        aria-labelledby="listbox-label"
        @click="toggleSelectMode"
      >
        <template v-if="displayValue">
          <template v-if="multiple && value.length > 0">
            <select-badge
              v-for="val in displayValue"
              :key="val"
              class="mr-1"
              @remove.stop="unselect(val)"
            >
              {{ val }}
            </select-badge>
          </template>
          <span
            v-else
            class="block truncate"
            :class="{
              '': !displayedError,
              'text-red-600': displayedError,
            }"
          >
            {{ displayValue }}
          </span>
        </template>
        <input
          v-else
          v-model="searchQuery"
          name="search"
          class="border-0 pl-0 py-0 block truncate w-full focus:outline-none focus:ring-0"
          :placeholder="placeholder"
          :disabled="disabled"
          :class="{
            'input-disabled': disabled,
            'pr-16': displayedError,
            'pr-10': value || searchQuery,
          }"
          @input="debouncedSearch"
        />
        <span
          v-if="(value || searchQuery) && searchable && !disabled"
          class="absolute inset-y-0 right-0"
          :class="displayedError ? 'mr-14' : 'mr-8'"
        >
          <!-- close button for cleaning -->
          <button
            type="button"
            class="absolute inset-y-0 right-0 flex items-center"
            @click.stop="clear"
          >
            <SolidXCircleIcon class="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        </span>
        <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <SolidSelectorIcon class="h-5 w-5 text-gray-400" />
        </span>
      </button>
      <div
        v-if="displayedError"
        class="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none"
      >
        <SolidExclamationCircleIcon class="h-5 w-5 text-red-500" />
      </div>

      <!-- Select popover, show/hide based on select state. -->
      <transition
        leave-active-class="transition ease-in duration-100"
        leave-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ul
          v-show="selecting && selectOptions.length > 0"
          ref="optionsRef"
          class="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
          tabindex="-1"
          role="listbox"
          aria-labelledby="listbox-label"
          aria-activedescendant="listbox-option-3"
        >
          <!--
            Select option, manage highlight styles based on mouseenter/mouseleave and keyboard navigation.

            Highlighted: "", Not Highlighted: "text-gray-900"
          -->
          <template v-for="category in categories" :key="category.value">
            <li
              v-if="category.value"
              class="text-gray-500 cursor-default text-xs select-none relative py-2 pl-3 pr-9"
            >
              {{ category.label }}
            </li>
            <template
              v-for="option in selectOptions.filter((opt) =>
                category.value ? opt.category == category.value : true,
              )"
              :key="option.value"
            >
              <slot name="option" :option="option" :select="select" :is-selected="isSelected">
                <li
                  class="text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9 hover:text-white hover:bg-blue"
                  role="option"
                  @click="select(option)"
                >
                  <span
                    class="block truncate"
                    :class="isSelected(option) ? 'font-semibold' : 'font-normal'"
                  >
                    {{ option.label }}
                  </span>

                  <!--
                    Checkmark, only display for selected option.

                    Highlighted: "text-white", Not Highlighted: "text-indigo-600"
                  -->
                  <span
                    v-if="isSelected(option)"
                    class="text-blue hover:text-white absolute inset-y-0 right-0 flex items-center pr-4"
                  >
                    <SolidCheckIcon class="h-5 w-5" />
                  </span>
                </li>
              </slot>
            </template>
          </template>
        </ul>
      </transition>
    </div>
    <p
      v-if="displayedError || help"
      class="mt-1 text-sm"
      :class="{
        'text-gray-500': !displayedError,
        'text-red-600': displayedError,
      }"
    >
      {{ displayedError || help }}
    </p>
  </div>
</template>

<script>
import { ref, toRefs, computed, onMounted, onUnmounted } from 'vue'
import { useSelect } from 'vue-batteries'
import { useFieldValidation } from '@/api/useValidation'
import SelectBadge from './SelectBadge.vue'
import { useDebounceFn } from '@vueuse/core'

function isObject(objValue) {
  return objValue && typeof objValue === 'object' && objValue.constructor === Object
}

const normalizeOptions = (options, valueAttr, labelAttr, categoryAttr) => {
  let normalizedOptions = []
  if (Array.isArray(options)) {
    // let objInArray = false
    options.forEach((opt) => {
      if (typeof opt !== 'object') {
        normalizedOptions.push({
          [valueAttr]: opt,
          [labelAttr]: opt,
          disabled: false,
          category: 'default',
        })
      } else {
        // objInArray = true
        normalizedOptions.push({
          value: opt[valueAttr],
          label: opt[labelAttr],
          disabled: false,
          category: opt[categoryAttr],
        })
      }
    })
  } else if (isObject(options)) {
    Object.entries(options).forEach(([key, value]) => {
      normalizedOptions.push({ value: key, label: value, disabled: false, category: 'default' })
    })
  } else {
    normalizedOptions = []
    return new Error('options must be an Array or an Object.')
  }
  return normalizedOptions
}

export default {
  name: 'TSelect',
  components: {
    SelectBadge,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    validator: {
      type: String,
      default: 'validator',
    },
    label: {
      type: String,
      default: '',
    },
    options: {
      type: [Array, Object],
      required: true,
    },
    categories: {
      type: Array,
      default: () => [
        {
          value: '',
          label: '',
        },
      ],
    },
    modelValue: {
      type: [Number, Boolean, String, Array, Object],
      default: null,
    },
    placeholder: {
      type: String,
      default: '-- Sélectionnez une option --',
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    leadingIcon: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [String, Array],
      default: '',
    },
    help: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    classWidth: {
      type: String,
      default: 'w-full',
    },
  },
  emits: ['update:modelValue', 'change'],
  setup(props, context) {
    const buttonRef = ref(null)
    const optionsRef = ref(null)
    const { modelValue, options } = toRefs(props)
    const searchQuery = ref('')
    const searchOptions = ref([])
    const defaultOptions = ref(normalizeOptions(options.value))
    const selectedOptions = computed(() =>
      searchQuery.value ? searchOptions.value : options.value,
    )
    const {
      toggleOption,
      value,
      isSelected,
      clearSelection,
      unselectOption,
      normalizedOptions: selectOptions,
    } = useSelect(context, modelValue, selectedOptions, props.multiple)
    const displayValue = computed(() => {
      if (
        (Array.isArray(value.value) && !value.value.length) ||
        value.value === '' ||
        value.value === null
      ) {
        return props.searchable ? '' : props.placeholder
      } else if (props.multiple) {
        return value.value.map((val) =>
          val ? selectOptions.value.find((opt) => opt.value === val).label : null,
        )
      } else {
        const selected = selectOptions.value.find((opt) => opt.value === value.value)
        return selected === undefined ? null : selected.label
      }
    })
    const selecting = ref(false)
    const searching = ref(false)
    if (props.searchable) {
      searchQuery.value = displayValue.value
    }

    // handle closing select when clicking outside
    const closeListIfClickedOutside = (event) => {
      const target = event.target
      if (selecting.value) {
        if (buttonRef.value.contains(target)) {
          return
        }
        if (!optionsRef.value.contains(target)) {
          selecting.value = false
        }
      }
    }
    onMounted(() => {
      window.addEventListener('mousedown', closeListIfClickedOutside)
    })

    onUnmounted(() => {
      window.removeEventListener('mousedown', closeListIfClickedOutside)
    })

    const search = () => {
      selecting.value = false
      const filter = searchQuery.value.toUpperCase()
      searchOptions.value = []
      for (let i = 0; i < defaultOptions.value.length; i++) {
        const option = defaultOptions.value[i]
        if (option.label.toUpperCase().indexOf(filter) > -1) {
          searchOptions.value.push(option)
        }
      }
      selecting.value = true
    }
    const debouncedSearch = useDebounceFn(search)

    return {
      toggleOption,
      unselectOption,
      selectOptions,
      searchOptions,
      selectedOptions,
      value,
      isSelected,
      clearSelection,
      displayValue,
      selecting,
      searching,
      searchQuery,
      buttonRef,
      optionsRef,
      ...useFieldValidation(props),
      debouncedSearch,
    }
  },
  computed: {
    hasLabel() {
      return this.label || this.$slots.label
    },
  },
  methods: {
    select(option) {
      this.toggleOption(option)
      if (!this.multiple) {
        this.selecting = false
      }
    },
    unselect(value) {
      const option = this.selectOptions.find((o) => o.label === value)
      this.unselectOption(option)
      this.selecting = false
    },
    toggleSelectMode() {
      if (this.disabled) {
        return
      }
      this.selecting = !this.selecting
    },
    clear() {
      this.searchQuery = ''
      this.clearSelection()
    },
  },
}
</script>
